import React, { useCallback } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { updateFilterData } from '#action/list-filter';
import { ResetAllButton } from '#component/general/list-filter/components/general/reset-all-button';
import {
  ChipsContainer,
  StyledChip,
  DeleteIconButton,
  ChipDeleteIcon
} from './styles';
import { momentFormat } from '#helper';

export const SelectedFiltersChips = ({
  filters,
  relatedTo,
  onRemove,
  showDateRange = false
}) => {
  const dispatch = useDispatch();
  const filterState = useSelector(s => s.listFilter?.[relatedTo], shallowEqual);
  const account = useSelector(s => s.account, shallowEqual);

  const getFilterValue = useCallback(filterName =>
    filterState?.[filterName]?.value || [], [filterState]);

  const handleRemoveChip = useCallback((filterName, filterLabel, option, getValueFn) => {
    const currentValue = getFilterValue(filterName);
    const optionValue = getValueFn(option);
    const newValue = currentValue.filter(v => getValueFn(v) !== optionValue);

    dispatch(updateFilterData(newValue, filterName, relatedTo));

    if (onRemove) {
      onRemove(newValue, filterName, relatedTo);
    }
  }, [dispatch, relatedTo, getFilterValue, onRemove]);

  const handleRemoveDateRange = useCallback(() => {
    dispatch(updateFilterData(null, 'start', relatedTo));
    dispatch(updateFilterData(null, 'end', relatedTo));

    if (onRemove) {
      onRemove(null, 'dateRange', relatedTo);
    }
  }, [dispatch, relatedTo, onRemove]);

  const allChips = filters.flatMap(filter => {
    const {
      name,
      label,
      getOptionLabel = option => option.label,
      getOptionValue = option => option.data?.id ?? option.label
    } = filter;

    const values = getFilterValue(name);

    return values.map(option => ({
      filterName: name,
      filterLabel: label,
      option,
      optionValue: getOptionValue(option),
      optionLabel: `${label}: ${getOptionLabel(option)}`,
      getOptionLabel,
      getOptionValue
    }));
  });

  const startDate = filterState?.start?.value;
  const endDate = filterState?.end?.value;
  const hasDateRange = showDateRange && startDate && endDate;

  const hasFilters = allChips.length > 0 || hasDateRange;

  if (!hasFilters) {
    return null;
  }

  return (
    <ChipsContainer>
      {hasDateRange && (
        <StyledChip
          key="dateRange"
          label={`Date: ${momentFormat(new Date(startDate), account, 'dateTimeShort')} - ${momentFormat(new Date(endDate), account, 'dateTimeShort')}`}
          variant="outlined"
          onDelete={handleRemoveDateRange}
          deleteIcon={
            <DeleteIconButton
              size="small"
              aria-label="Remove date range"
            >
              <ChipDeleteIcon fontSize="small" />
            </DeleteIconButton>
          }
        />
      )}
      {allChips.map(chip => (
        <StyledChip
          key={`${chip.filterName}-${chip.optionValue}`}
          label={chip.optionLabel}
          variant="outlined"
          onDelete={() => handleRemoveChip(
            chip.filterName,
            chip.filterLabel,
            chip.option,
            chip.getOptionValue
          )}
          deleteIcon={
            <DeleteIconButton
              size="small"
              aria-label={`Remove ${chip.optionLabel}`}
            >
              <ChipDeleteIcon fontSize="small" />
            </DeleteIconButton>
          }
        />
      ))}
      {hasFilters && (
        <ResetAllButton
          filters={filters}
          relatedTo={relatedTo}
          showDateRange={showDateRange}
          onReset={onRemove}
        />
      )}
    </ChipsContainer>
  );
};
